import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { P2GBox, P2GFade, P2GLink, P2GLinkGroup, P2GGrid, SvgDrag } from '@parcel2go/core-ui';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';

interface props {
  id: string;
  p?: number;
  backgroundColour?: string;
  filled?: boolean;
  bordered?: boolean;
  title?: ReactNode;
  width: string | number | undefined;
  onClose?: () => void;
  additionalLinks?: ReactNode[];
  children: ReactNode;
}

const Draggable: React.FC<props> = (props) => {
  const [position, setPosition] = useLocalStorage(`SS-DRAGGABLE-${props.id}`, { x: 0, y: 0 });
  const elementRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    if (!loaded && elementRef && elementRef.current) {
      elementRef.current.style.transform = `translate(${position.x}px, ${position.y}px)`;
      setLoaded(true);
    }
  }, [elementRef, loaded]);

  const onMouseDown = useCallback(
    (_: React.MouseEvent) => {
      const pos = { ...position };
      const onMouseMove = (event: MouseEvent) => {
        pos.x += event.movementX;
        pos.y += event.movementY;

        const element = elementRef.current;
        if (element) {
          element.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
        }
        setPosition(pos);
      };
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [position, setPosition, elementRef]
  );

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 9999,
        width: props.width
      }}
      ref={elementRef}
    >
      <P2GFade in={loaded}>
        <P2GBox backgroundColour={props.backgroundColour} p={props.p} filled={props.filled} bordered={props.bordered}>
          <P2GGrid container alignContent="center" alignItems="center" gap={2} wrap="nowrap">
            <P2GGrid item flexGrow={1}>
              <div onMouseDown={onMouseDown}>{props.title}</div>
            </P2GGrid>
            <P2GGrid item flexShrink={1}>
              <div onMouseDown={onMouseDown}>
                <CdnIconProxy size="xs" palette="primary" src="/theme/icons/Drag.svg">
                  <SvgDrag />
                </CdnIconProxy>
              </div>
            </P2GGrid>
          </P2GGrid>
          {loaded && (
            <>
              <P2GBox pt={1}>{props.children}</P2GBox>
              <P2GBox textAlign="center" pt={1}>
                <P2GLinkGroup
                  links={[
                    <P2GLink onClick={props.onClose}>
                      {t('UNPIN_MENU_LINK', { defaultValue: 'Unpin This Menu' })}
                    </P2GLink>,
                    ...(props.additionalLinks?.map((link, index) => <Fragment key={index}>{link}</Fragment>) || [])
                  ]}
                />
              </P2GBox>
            </>
          )}
        </P2GBox>
      </P2GFade>
    </div>
  );
};

export { Draggable };
